// eslint-disable-next-line import/no-extraneous-dependencies
import { Observable, of } from "rxjs";
import { inject, injectable } from "tsyringe";
import { map } from "rxjs/operators";
import { DocumentsDownloadEndpoints } from "src/containers/documents-download/constants/endpoints";
import { autoBind } from "src/shared/decorators/autobind";
import { HttpClient } from "src/infra/http/client";
import { replaceString } from "src/libs/replace-string";
import { DocumentDetail } from "src/containers/documents-download/models/document-detail.model";

export type GetEpsDocumentListPayload = {
    insuredCode: number;
};

export type GetSoatDocumentListPayload = {
    policyNumber: number;
};

export type GetPgaDocumentListPayload = {
    userIdentity: string;
    systemCode: string;
    policyId: number;
};

export type GetEpsDocumentDetailPayload = {
    userIdentity: string;
    planNumber: number;
    contractorId: number;
};

export type GetSoatDocumentDetailPayload = {
    userIdentity: string;
    systemCode: string;
    policyId: number;
};

export type GetPgaDocumentDetailPayload = {
    userIdentity: string;
    systemCode: string;
    policyId: number;
    fileId: string;
};

export type GetHealthDocumentDetailPayload = {
    systemCode: string;
    policyId: number;
    fileId: string;
};

@injectable()
export class DocumentsDownloadRepository {
    constructor(@inject("HttpClient") private http: HttpClient) {}

    @autoBind
    getEpsDocumentList(insuredCode: number): Observable<Array<object>> {
        return of([
            {
                id: insuredCode,
                documentType: "Plan",
                fileName: `EPS-${insuredCode}`,
            },
        ]);
    }

    @autoBind
    getSoatDocumentList(policyNumber: number): Observable<Array<object>> {
        return of([
            {
                id: policyNumber,
                documentType: "Certificado",
                fileName: `SOAT-${policyNumber}`,
            },
        ]);
    }

    @autoBind
    getEpsCertificateDocumentDetail(
        planNumber: number,
        userIdentity: string
    ): Observable<DocumentDetail> {
        const url = replaceString(
            DocumentsDownloadEndpoints.GetPgaCertificateDocumentDetail,
            {
                ":userIdentity": userIdentity,
            }
        );
        const headers = this.http.httpUtils.getMsBaseHeaders(
            userIdentity,
            "MEP"
        );
        return this.http
            .getJSON<Record<string, any>>(url, {
                isPublicRequest: false,
                params: {
                    type: "EPS",
                    "plan-number": planNumber,
                },
                customHeader: {
                    ...this.http.httpUtils.getBaseHeaders(),
                    ...headers,
                },
            })
            .pipe(
                map((detail) => DocumentDetail.fromJson<DocumentDetail>(detail))
            );
    }

    @autoBind
    getPgaDocumentList(
        payload: GetPgaDocumentListPayload
    ): Observable<Array<object>> {
        const { userIdentity, systemCode, policyId } = payload;
        const url = replaceString(DocumentsDownloadEndpoints.GetPgaList, {
            ":userIdentity": userIdentity,
            ":policyId": policyId,
        });
        const headers = this.http.httpUtils.getMsBaseHeaders(
            userIdentity,
            "MEP"
        );
        return (
            this.http
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .getJSON<Array<Record<string, any>>>(url, {
                    isPublicRequest: false,
                    params: { "system-code": systemCode },
                    customHeader: {
                        ...this.http.httpUtils.getBaseHeaders(),
                        ...headers,
                    },
                })
        );
    }

    @autoBind
    getHealthDocumentList(
        payload: GetPgaDocumentListPayload
    ): Observable<Array<object>> {
        const { policyId, systemCode } = payload;
        const url = replaceString(DocumentsDownloadEndpoints.GetHealthList, {
            ":policyId": policyId,
        });
        return this.http.getJSON<Array<Record<string, any>>>(url, {
            host: process.env.REACT_APP_APIMGMT_URL,
            isPublicRequest: false,
            params: { "system-code": systemCode },
            customHeader: {
                ...this.http.httpUtils.getBaseHeaders(),
                ...this.http.httpUtils.getMsBaseHeaders(
                    window.userIdentity,
                    "MEP"
                ),
                Accept: "*",
            },
        });
    }

    @autoBind
    getHealthDocumentDetail(
        payload: GetPgaDocumentDetailPayload
    ): Observable<object> {
        const { policyId, systemCode, fileId } = payload;
        const url = replaceString(
            DocumentsDownloadEndpoints.GetHealthDocumentDetail,
            {
                ":policyId": policyId,
                ":fileId": fileId,
            }
        );
        return this.http.get<Record<string, any>>(url, {
            host: process.env.REACT_APP_APIMGMT_URL,
            isPublicRequest: false,
            params: {
                "system-code": systemCode,
            },
            customHeader: {
                ...this.http.httpUtils.getBaseHeaders(),
                ...this.http.httpUtils.getMsBaseHeaders(
                    window.userIdentity,
                    "MEP"
                ),
                Accept: "*",
            },
        });
    }

    @autoBind
    getEpsDocumentDetail(
        payload: GetEpsDocumentDetailPayload
    ): Observable<Record<string, any>> {
        const { userIdentity, planNumber, contractorId } = payload;
        const url = replaceString(
            DocumentsDownloadEndpoints.GetEpsDocumentDetail,
            {
                ":userIdentity": userIdentity,
                ":planNumber": planNumber,
            }
        );
        return (
            this.http
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .getJSON<Record<string, any>>(url, {
                    host: process.env.REACT_APP_APIMGMT_URL,
                    isPublicRequest: false,
                    params: { "contractor-id": contractorId },
                    customHeader: {
                        ...this.http.httpUtils.getBaseHeaders(),
                        ...this.http.httpUtils.getMsBaseHeaders(
                            window.userIdentity,
                            "MEP"
                        ),
                        Accept: "*/*",
                    },
                })
        );
    }

    @autoBind
    getSoatDocumentDetail(
        payload: GetPgaDocumentDetailPayload
    ): Observable<object> {
        const CERTIFICATE_TYPE_SOAT = "SOAT";
        const { userIdentity, systemCode, policyId } = payload;
        const url = replaceString(
            DocumentsDownloadEndpoints.GetSoatDocumentDetail,
            {
                ":userIdentity": userIdentity,
            }
        );
        return (
            this.http
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .getJSON<Record<string, any>>(url, {
                    isPublicRequest: false,
                    params: {
                        type: CERTIFICATE_TYPE_SOAT,
                        "system-code": systemCode,
                        "policy-id": policyId,
                    },
                    customHeader: {
                        ...this.http.httpUtils.getBaseHeaders(),
                        ...this.http.httpUtils.getMsBaseHeaders(
                            userIdentity,
                            "MEP"
                        ),
                    },
                })
        );
    }

    @autoBind
    getSoatDocumentDetailByEmail(
        payload: GetPgaDocumentDetailPayload
    ): Observable<object> {
        const CERTIFICATE_TYPE_SOAT = "SOAT";
        const { userIdentity, systemCode, policyId } = payload;
        const url = replaceString(
            DocumentsDownloadEndpoints.GetSoatDocumentDetail,
            {
                ":userIdentity": userIdentity,
            }
        );
        return (
            this.http
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .post<Record<string, any>>(url, {
                    isPublicRequest: false,
                    params: {
                        type: CERTIFICATE_TYPE_SOAT,
                        "system-code": systemCode,
                        "policy-id": policyId,
                    },
                    customHeader: {
                        ...this.http.httpUtils.getBaseHeaders(),
                        ...this.http.httpUtils.getMsBaseHeaders(
                            userIdentity,
                            "MEP"
                        ),
                    },
                })
        );
    }

    @autoBind
    getPgaDocumentDetail(
        payload: GetPgaDocumentDetailPayload
    ): Observable<object> {
        const { userIdentity, systemCode, policyId, fileId } = payload;
        const url = replaceString(
            DocumentsDownloadEndpoints.GetPgaDocumentDetail,
            {
                ":userIdentity": userIdentity,
                ":policyId": policyId,
                ":fileId": fileId,
            }
        );
        return (
            this.http
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .getJSON<Record<string, any>>(url, {
                    isPublicRequest: false,
                    params: { "system-code": systemCode },
                    customHeader: {
                        ...this.http.httpUtils.getBaseHeaders(),
                        ...this.http.httpUtils.getMsBaseHeaders(
                            userIdentity,
                            "MEP"
                        ),
                    },
                })
        );
    }
}
